/**
 * Colour, type and spacing come from @finderly/design-tokens at runtime —
 * unlike the widget, the portal has no 32 KB budget, so it imports the real
 * tokens instead of copying literals. They become CSS custom properties on
 * :root (light), under prefers-color-scheme (auto dark) and under
 * [data-theme] (explicit choice), so styles.css never names a hex value.
 */
import { TonalPalettes, colorsDark, colorsLight, fontSizes, lineHeights, spacing } from '@finderly/design-tokens';

type Palette = keyof typeof TonalPalettes;
const tone = (palette: Palette, t: number | 'root'): string =>
  (TonalPalettes[palette] as Record<string | number, string>)[t] as string;

/** Semantic roles the stylesheet uses, resolved per theme. */
function roles(mode: 'light' | 'dark'): Record<string, string> {
  const c = mode === 'light' ? colorsLight : colorsDark;
  const n = (t: number) => tone('neutral', t);
  return mode === 'light'
    ? {
        bg: n(99),
        panel: n(100),
        'panel-sunk': n(97),
        line: n(90),
        'line-strong': n(80),
        ink: n(10),
        text: n(25),
        muted: n(45),
        faint: n(60),
        accent: tone('primary', 50),
        'accent-ink': tone('primary', 40),
        'accent-soft': tone('primary', 95),
        'on-accent': n(100),
        focus: tone('primary', 55),
        ok: tone('success', 40),
        'ok-soft': tone('success', 98),
        warn: '#8a5a00',
        'warn-soft': '#fff4de',
        bad: c.error,
        'bad-ink': tone('error', 40),
        'bad-soft': tone('error', 98),
        info: tone('info', 50),
        'info-soft': tone('info', 98),
        rail: n(10),
        'rail-text': n(80),
        'rail-strong': n(100),
        'rail-line': n(25),
        code: n(12),
        'code-text': n(92),
        // Outcome-split categorical slots, validated with the dataviz
        // validator against the light panel (#ffffff): all checks pass; slot 4
        // is under 3:1, so the split always ships visible labels + counts.
        'viz-1': tone('primary', 50),
        'viz-2': tone('info', 55),
        'viz-3': '#eb6834',
        'viz-4': '#1baf7a',
      }
    : {
        bg: n(10),
        panel: n(15),
        'panel-sunk': n(12),
        line: n(25),
        'line-strong': n(35),
        ink: n(100),
        text: n(92),
        muted: n(70),
        faint: n(55),
        accent: tone('primary', 80),
        'accent-ink': tone('primary', 90),
        'accent-soft': tone('primary', 20),
        'on-accent': tone('primary', 10),
        focus: tone('primary', 80),
        ok: tone('success', 80),
        'ok-soft': tone('success', 20),
        warn: '#ffc766',
        'warn-soft': '#2e2100',
        bad: c.error,
        'bad-ink': tone('error', 80),
        'bad-soft': tone('error', 20),
        info: tone('info', 80),
        'info-soft': tone('info', 20),
        rail: n(5),
        'rail-text': n(70),
        'rail-strong': n(100),
        'rail-line': n(22),
        code: n(5),
        'code-text': n(92),
        // Same four hues stepped for the dark panel (neutral 15); all pass.
        'viz-1': tone('primary', 60),
        'viz-2': tone('info', 60),
        'viz-3': '#d95926',
        'viz-4': '#199e70',
      };
}

function block(vars: Record<string, string>): string {
  return Object.entries(vars)
    .map(([k, v]) => `--${k}:${v};`)
    .join('');
}

export function themeCss(): string {
  const scale = {
    'fs-display': `${fontSizes.headlineMedium}px`,
    'lh-display': `${lineHeights.headlineMedium}px`,
    'fs-title': `${fontSizes.titleLarge}px`,
    'lh-title': `${lineHeights.titleLarge}px`,
    'fs-subtitle': `${fontSizes.titleSmall}px`,
    'fs-body': `${fontSizes.bodyMedium}px`,
    'lh-body': `${lineHeights.bodyMedium}px`,
    'fs-small': `${fontSizes.bodySmall}px`,
    'fs-label': `${fontSizes.labelMedium}px`,
    'fs-tiny': `${fontSizes.labelSmall}px`,
    's1': `${spacing[100]}px`,
    's2': `${spacing[200]}px`,
    's3': `${spacing[300]}px`,
    's4': `${spacing[400]}px`,
    's5': `${spacing[500]}px`,
    's6': `${spacing[600]}px`,
    's8': `${spacing[800]}px`,
    's10': `${spacing[1000]}px`,
    'r-sm': `${spacing[150]}px`,
    'r-md': `${spacing[250]}px`,
    'r-lg': `${spacing[400]}px`,
    'brand-a': tone('primary', 'root'),
    'brand-b': tone('secondary', 55),
  };
  const light = block({ ...scale, ...roles('light') });
  const dark = block(roles('dark'));
  return [
    `:root{${light}color-scheme:light;}`,
    `@media (prefers-color-scheme: dark){:root:not([data-theme="light"]){${dark}color-scheme:dark;}}`,
    `:root[data-theme="dark"]{${dark}color-scheme:dark;}`,
  ].join('\n');
}

export type ThemeChoice = 'auto' | 'light' | 'dark';
const THEME_KEY = 'fx-partner-theme';

export function readTheme(): ThemeChoice {
  try {
    const v = window.localStorage.getItem(THEME_KEY);
    return v === 'light' || v === 'dark' ? v : 'auto';
  } catch {
    return 'auto';
  }
}

export function applyTheme(choice: ThemeChoice): void {
  const root = document.documentElement;
  if (choice === 'auto') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', choice);
  try {
    if (choice === 'auto') window.localStorage.removeItem(THEME_KEY);
    else window.localStorage.setItem(THEME_KEY, choice);
  } catch {
    /* ignore */
  }
}

export function installTheme(): void {
  const style = document.createElement('style');
  style.id = 'fx-theme';
  style.textContent = themeCss();
  document.head.prepend(style);
  applyTheme(readTheme());
}
